import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  MassActionConfig,
  OptionType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import {
  CompanyLocationExceptionPreviewOutDTO,
  ExceptionTimeIntervalOutDTO,
  LocationListPreviewOutDTO,
  LocationSelectModeEnum,
  Pageable,
} from '@api/ringil-api.v2';
import {
  dayjsFromApiUtcInTz,
  dayjsFromLocalDateString,
  formatDateFromDayjsWithLocale,
  formatTimeFromDayjsWithLocale,
} from '@utils/dateWithTz';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import COText from '@app/pages/COText/COText.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import NewExceptionSidebar from './sidebars/NewExceptionSidebar';
import { Button } from 'primereact/button';

interface ExceptionFilter {
  dateFrom?: string;
  dateTo?: string;
  locationIds?: string[];
  createdByName?: string;
}

// RingilDataTable selection keys rows by `id`; exceptions expose locationExceptionId
type ExceptionRow = CompanyLocationExceptionPreviewOutDTO & { id: string };

// FE-only sentinel for the synthetic "All locations" item; translated to locationSelectMode in loadData
const ALL_LOCATIONS_OPTION = '__ALL_LOCATIONS__';

const ExceptionList: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { getCompanyLocationExceptions, deleteCompanyLocationExceptions, getLoVCompanyLocations } = useContext(ApiContext);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: ExceptionRow[]; clear: () => void }>(null);
  const [visibleNewException, setVisibleNewException] = useState(false);
  const [deleteIds, setDeleteIds] = useState<string[]>([]);
  const [locationOptions, setLocationOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    getLoVCompanyLocations(false, data =>
      setLocationOptions([
        { label: tr('ExceptionList.allLocations', 'All locations'), value: ALL_LOCATIONS_OPTION, active: true },
        ...data.map(l => ({ label: l.name, value: l.id, active: true })),
      ]),
    );
  }, []);

  const loadData = (filter: ExceptionFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<ExceptionRow>) => void) => {
    const selected = filter?.locationIds || [];
    const includeAllLocations = selected.includes(ALL_LOCATIONS_OPTION);
    const realLocationIds = selected.filter(id => id !== ALL_LOCATIONS_OPTION);
    getCompanyLocationExceptions(
      pageable,
      {
        dateFrom: filter?.dateFrom,
        dateTo: filter?.dateTo,
        locationIds: realLocationIds,
        locationSelectMode: includeAllLocations ? LocationSelectModeEnum.ALL_LOCATIONS : undefined,
        createdByName: filter?.createdByName,
      },
      data => onSuccess({ ...data, data: data.data.map(t => ({ ...t, id: t.locationExceptionId })) }),
    );
  };

  const confirmDelete = () => {
    deleteCompanyLocationExceptions(deleteIds, () => {
      setDeleteIds([]);
      tableRef.current?.clear();
      tableRef.current?.reloadData();
    });
  };

  const renderOpeningTime = (isClosed: boolean, openingTime?: ExceptionTimeIntervalOutDTO[]) => {
    if (isClosed) return tr('ExceptionList.closed', 'Closed');
    return openingTime
      .map(item =>
        item.localStartTime && item.localEndTime
          ? `${item.localStartTime} - ${item.localEndTime}`
          : `${formatTimeFromDayjsWithLocale(dayjsFromApiUtcInTz(item.since, 'Europe/Prague'))} - ${formatTimeFromDayjsWithLocale(dayjsFromApiUtcInTz(item.till, 'Europe/Prague'))}`,
      )
      .join(', ');
  };

  const renderLocalities = (localities: LocationListPreviewOutDTO[]) => {
    if (localities.length === 0) return tr('ExceptionList.allLocations', 'All locations');
    return localities.map((item, index) => <div key={index}>{item.localityName}</div>);
  };

  const renderRamps = (localities: LocationListPreviewOutDTO[]) => {
    if (localities.length === 0) return tr('ExceptionList.allRamps', 'All ramps');
    return localities.map((item, index) => (
      <div key={index} className="whitespace-nowrap overflow-hidden text-ellipsis">
        {item.rampsNames.length === 0 ? tr('ExceptionList.allRamps', 'All ramps') : item.rampsNames.join(', ')}
      </div>
    ));
  };

  const renderDates = (row: ExceptionRow) => {
    const days = (
      row.localDates && row.localDates.length > 0
        ? row.localDates.map(d => dayjsFromLocalDateString(d))
        : row.dates.map(d => dayjsFromApiUtcInTz(d, 'Europe/Prague'))
    ).sort((a, b) => (a.isBefore(b) ? -1 : 1));

    // collapse consecutive days into intervals
    const groups: { start: ReturnType<typeof dayjsFromLocalDateString>; end: ReturnType<typeof dayjsFromLocalDateString> }[] = [];
    days.forEach(day => {
      const last = groups[groups.length - 1];
      if (last && day.isSame(last.end.add(1, 'day'), 'day')) {
        last.end = day;
      } else {
        groups.push({ start: day, end: day });
      }
    });

    return (
      <div>
        {groups.map((g, index) => (
          <div key={index}>
            {g.start.isSame(g.end, 'day')
              ? formatDateFromDayjsWithLocale(g.start)
              : `${formatDateFromDayjsWithLocale(g.start)} – ${formatDateFromDayjsWithLocale(g.end)}`}
          </div>
        ))}
      </div>
    );
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.Calendar,
      placeholder: tr('ExceptionList.dateOrInterval', 'Date or interval'),
      value: ['dateFrom', 'dateTo'],
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('ExceptionList.locality', 'Locality'),
      value: ['locationIds'],
      options: locationOptions,
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('ExceptionList.createdBy', 'Created by'),
      value: ['createdByName'],
    },
  ];

  const columnsConfig: ColumnConfig<ExceptionRow>[] = [
    {
      id: 'date',
      field: 'date',
      header: 'ExceptionList.dateOrInterval',
      headerDefault: 'Date or interval',
      width: '12%',
      sortable: true,
      isDefaultSort: true,
      order: 1,
      render: renderDates,
    },
    {
      id: 'exception',
      field: 'exception',
      header: 'ExceptionList.exception',
      headerDefault: 'Exception',
      width: '28%',
      sortable: false,
      order: 2,
      render: row => (
        <div>
          <div className="flex flex-row gap-3 items-center">
            {renderOpeningTime(row.closed, row.openingHours)}
            {row.isOnlyForExternalPartners === true && (
              <COBadge variant="filled" type="neutral">
                <COText label={tr('ExceptionList.externi', 'Externí')} />
              </COBadge>
            )}
          </div>
          <div>{row.exceptionName}</div>
        </div>
      ),
    },
    {
      id: 'locality',
      field: 'location',
      header: 'ExceptionList.locality',
      headerDefault: 'Locality',
      width: '20%',
      sortable: true,
      order: 3,
      render: row => <div>{renderLocalities(row.localities)}</div>,
    },
    {
      id: 'ramps',
      field: 'ramps',
      header: 'ExceptionList.ramps',
      headerDefault: 'Ramps',
      width: '18%',
      sortable: false,
      order: 4,
      render: row => <div>{renderRamps(row.localities)}</div>,
    },
    {
      id: 'createdBy',
      field: 'createdAt',
      header: 'ExceptionList.createdBy',
      headerDefault: 'Created by',
      width: '14%',
      sortable: true,
      order: 5,
      render: row => (
        <div>
          <div>{row.createdBy}</div>
          <div>{formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(row.createdAt, 'Europe/Prague'))}</div>
        </div>
      ),
    },
    {
      id: 'action',
      field: 'action',
      header: 'ExceptionList.actions',
      headerDefault: '',
      width: '8%',
      sortable: false,
      order: 6,
      render: row => (
        <RingilButton mode="text-only" label={tr('ExceptionList.delete', 'Delete')} onClick={() => setDeleteIds([row.locationExceptionId])} />
      ),
    },
  ];

  const massActionConfig: MassActionConfig<ExceptionRow>[] = [
    {
      label: tr('ExceptionList.delete', 'Delete'),
      onClick: () => {
        const ids = (tableRef.current?.selectedObjects || []).map(t => t.locationExceptionId);
        if (ids.length > 0) setDeleteIds(ids);
      },
    },
  ];

  return (
    <>
      <BreadCrumbComponent items={[{ label: tr('ExceptionList.exceptions', 'Exceptions') }]} />
      <div className="flex items-center mb-6">
        <COHeadline title={tr('ExceptionList.openingTimeExceptions', 'Opening time exceptions')} variant="h1" />
        <Button
          label={tr('ExceptionList.newException', 'New Exception')}
          icon="pi pi-plus"
          className="p-button-text !text-[#FE5000] hover:!text-orange-600 !inline-flex !items-center"
          onClick={() => setVisibleNewException(true)}
        />
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadData}
        filterName="exception_list"
        massActionConfig={massActionConfig as MassActionConfig<unknown>[]}
      />

      <NewExceptionSidebar
        visible={visibleNewException}
        onHide={() => setVisibleNewException(false)}
        onAdded={() => {
          setVisibleNewException(false);
          tableRef.current?.reloadData();
        }}
      />

      <ConfirmDialog
        visible={deleteIds.length > 0}
        dialogTitle={tr('ExceptionList.confirmDeleteActions', 'Confirm delete actions')}
        dialogMessage={
          deleteIds.length > 1
            ? tr('ExceptionList.doYouWantToDeleteMultipleItems', 'Do you want to delete multiple items?')
            : tr('ExceptionList.doYouWantToDeleteException', 'Do you want to delete exception?')
        }
        onCancel={() => setDeleteIds([])}
        onConfirm={confirmDelete}
        onAcceptButtonText={tr('ExceptionList.confirm', 'Confirm')}
        onCancelButtonText={tr('ExceptionList.back', 'Back')}
      />
    </>
  );
};

export default ExceptionList;